import React, { useCallback, useEffect, useMemo, useState } from 'react';
import crmClient from './crmClient';

const EMPTY_GEN = { keyword: '', type: 'article', location: '', instructions: '' };

const STATUS_LABEL = {
  draft: 'Draft',
  needs_review: 'Needs review',
  approved: 'Approved',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

// Format a generation cost (in cents) as dollars or cents.
const formatCost = cents => {
  const n = Number(cents);
  if (!n || n <= 0) return '—';
  if (n >= 100) return '$' + (n / 100).toFixed(2);
  return n.toFixed(n < 1 ? 3 : 2) + '¢';
};

export default function SeoView() {
  const [meta, setMeta] = useState(null);
  const [articles, setArticles] = useState([]);
  const [gen, setGen] = useState(EMPTY_GEN);
  const [genBusy, setGenBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [editor, setEditor] = useState(null); // currently open article
  const [editorBusy, setEditorBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [ideas, setIdeas] = useState(null);
  const [ideasBusy, setIdeasBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const notify = useCallback((text, kind) => {
    setMessage({ text, kind: kind || 'info' });
    window.clearTimeout(notify._t);
    notify._t = window.setTimeout(() => setMessage(null), 6000);
  }, []);

  const loadMeta = useCallback(async () => {
    try { setMeta(await crmClient.seoMeta()); } catch (e) { /* noop */ }
  }, []);

  const loadArticles = useCallback(async () => {
    try {
      const res = await crmClient.listSeoArticles(filter ? { status: filter } : {});
      setArticles((res && res.articles) || []);
    } catch (e) { notify(e.message || 'Failed to load articles', 'error'); }
  }, [filter, notify]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadArticles(); }, [loadArticles]);

  const aiConfigured = meta && meta.ai_configured;
  const wpConfigured = meta && meta.wordpress_configured;
  const categories = (meta && meta.categories) || [];

  const generate = async () => {
    if (!gen.keyword.trim()) { notify('Enter a target keyword or topic.', 'error'); return; }
    setGenBusy(true);
    try {
      const res = await crmClient.generateSeoArticle(gen);
      const flags = (res && res.lint_flags) || [];
      notify(
        'Draft created' + (flags.length ? ` (${flags.length} style flag${flags.length > 1 ? 's' : ''} to review)` : ''),
        flags.length ? 'info' : 'success'
      );
      setGen(EMPTY_GEN);
      await loadArticles();
      if (res && res.article) openEditor(res.article);
    } catch (e) {
      notify(e.message || 'Generation failed', 'error');
    } finally { setGenBusy(false); }
  };

  const suggest = async () => {
    setIdeasBusy(true);
    try {
      const res = await crmClient.suggestSeoKeywords({ topic: gen.keyword });
      setIdeas((res && res.ideas) || []);
    } catch (e) { notify(e.message || 'Could not suggest keywords', 'error'); }
    finally { setIdeasBusy(false); }
  };

  const openEditor = article => {
    setEditor({
      ...article,
      secondary_keywords: (article.secondary_keywords || []).join(', '),
      tags: (article.tags || []).join(', ')
    });
  };

  const saveEditor = async () => {
    if (!editor) return;
    setEditorBusy(true);
    try {
      const body = {
        title: editor.title,
        meta_title: editor.meta_title,
        meta_description: editor.meta_description,
        target_keyword: editor.target_keyword,
        slug: editor.slug,
        category: editor.category,
        cta_url: editor.cta_url,
        body_html: editor.body_html,
        secondary_keywords: String(editor.secondary_keywords || '').split(',').map(s => s.trim()).filter(Boolean),
        tags: String(editor.tags || '').split(',').map(s => s.trim()).filter(Boolean)
      };
      const res = await crmClient.updateSeoArticle(editor.crm_seo_article_id, body);
      if (res && res.article) openEditor(res.article);
      await loadArticles();
      notify('Saved', 'success');
    } catch (e) { notify(e.message || 'Save failed', 'error'); }
    finally { setEditorBusy(false); }
  };

  const approve = async article => {
    setBusy(true);
    try {
      await crmClient.approveSeoArticle(article.crm_seo_article_id);
      await loadArticles();
      if (editor && editor.crm_seo_article_id === article.crm_seo_article_id) {
        setEditor({ ...editor, status: 'approved' });
      }
      notify('Approved. Ready to publish.', 'success');
    } catch (e) { notify(e.message || 'Approve failed', 'error'); }
    finally { setBusy(false); }
  };

  const publish = async article => {
    setBusy(true);
    try {
      const res = await crmClient.publishSeoArticle(article.crm_seo_article_id);
      await loadArticles();
      if (res && res.article && editor && editor.crm_seo_article_id === article.crm_seo_article_id) openEditor(res.article);
      if (res && res.note) notify(res.note, 'info');
      else notify('Published to the site.', 'success');
    } catch (e) { notify(e.message || 'Publish failed', 'error'); }
    finally { setBusy(false); }
  };

  const genImage = async () => {
    if (!editor) return;
    setImageBusy(true);
    try {
      const res = await crmClient.generateSeoArticleImage(editor.crm_seo_article_id);
      if (res && res.article) openEditor(res.article);
      await loadArticles();
      notify('Featured image generated', 'success');
    } catch (e) { notify(e.message || 'Image generation failed', 'error'); }
    finally { setImageBusy(false); }
  };

  const remove = async article => {
    if (!window.confirm('Delete this article?')) return;
    setBusy(true);
    try {
      await crmClient.deleteSeoArticle(article.crm_seo_article_id);
      if (editor && editor.crm_seo_article_id === article.crm_seo_article_id) setEditor(null);
      await loadArticles();
    } catch (e) { notify(e.message || 'Delete failed', 'error'); }
    finally { setBusy(false); }
  };

  const lintFlags = editor && editor.generation_meta && editor.generation_meta.lint_flags;

  const statusOptions = useMemo(() => (meta && meta.statuses) || Object.keys(STATUS_LABEL), [meta]);

  return (
    <div className="crm-seo">
      {message && <div className={`crm-seo-toast crm-seo-toast--${message.kind}`}>{message.text}</div>}

      <div className="crm-seo-head">
        <div>
          <h3>Content &amp; SEO</h3>
          <p className="crm-seo-sub">
            Generate genuinely useful freight articles and landing pages, review them, then publish to the site so
            Gophr shows up in Google and AI search. Every article needs a human approval before it goes live.
          </p>
        </div>
      </div>

      {!aiConfigured && (
        <div className="crm-seo-banner crm-seo-banner--warn">
          AI content generation is not configured on the server (missing OpenAI key).
        </div>
      )}
      {aiConfigured && !wpConfigured && (
        <div className="crm-seo-banner">
          WordPress is not connected yet, so articles can be written, reviewed, and approved, but not auto-published.
          Add the site credentials on the server to enable one-click publishing.
        </div>
      )}

      <div className="crm-seo-grid">
        {/* Generator */}
        <div className="crm-seo-card">
          <h4>Create an article</h4>
          <label>Target keyword or topic</label>
          <input
            type="text"
            value={gen.keyword}
            placeholder="e.g. same day flatbed delivery in Baton Rouge"
            onChange={e => setGen({ ...gen, keyword: e.target.value })}
          />
          <div className="crm-seo-row">
            <div style={{ flex: 1 }}>
              <label>Type</label>
              <select value={gen.type} onChange={e => setGen({ ...gen, type: e.target.value })}>
                <option value="article">Article (how-to / guide)</option>
                <option value="landing_page">Service landing page</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label>Location (optional)</label>
              <input
                type="text"
                value={gen.location}
                placeholder="e.g. Houston, TX"
                onChange={e => setGen({ ...gen, location: e.target.value })}
              />
            </div>
          </div>
          <label>Extra instructions (optional)</label>
          <textarea
            rows={2}
            value={gen.instructions}
            placeholder="Angle, audience, points to cover..."
            onChange={e => setGen({ ...gen, instructions: e.target.value })}
          />
          <div className="crm-seo-actions">
            <button className="crm-seo-btn crm-seo-btn--primary" disabled={genBusy || !aiConfigured} onClick={generate}>
              {genBusy ? 'Writing…' : 'Generate draft'}
            </button>
            <button className="crm-seo-btn" disabled={ideasBusy || !aiConfigured} onClick={suggest}>
              {ideasBusy ? 'Thinking…' : 'Suggest keywords'}
            </button>
          </div>

          {ideas && (
            <div className="crm-seo-ideas">
              <div className="crm-seo-ideas-head">
                <strong>Keyword ideas</strong>
                <button className="crm-seo-link" onClick={() => setIdeas(null)}>hide</button>
              </div>
              {!ideas.length && <p className="crm-seo-muted">No ideas returned.</p>}
              {ideas.map((it, i) => (
                <div className="crm-seo-idea" key={i}>
                  <button
                    className="crm-seo-idea-kw"
                    onClick={() => setGen({ ...gen, keyword: it.keyword, type: it.type === 'landing_page' ? 'landing_page' : 'article' })}
                    title="Use this keyword"
                  >
                    {it.keyword}
                  </button>
                  <span className={`crm-seo-chip crm-seo-chip--${it.intent === 'commercial' ? 'commercial' : 'info'}`}>{it.intent}</span>
                  <span className="crm-seo-idea-why">{it.why}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Article list */}
        <div className="crm-seo-card">
          <div className="crm-seo-list-head">
            <h4>Articles</h4>
            <select value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="">All statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
            </select>
          </div>
          {!articles.length && <p className="crm-seo-muted">No articles yet. Generate your first draft on the left.</p>}
          <table className="crm-seo-table">
            <tbody>
              {articles.map(a => (
                <tr key={a.crm_seo_article_id}>
                  <td className="crm-seo-cell-title">
                    <button className="crm-seo-link" onClick={() => openEditor(a)}>{a.title}</button>
                    <div className="crm-seo-kw">{a.target_keyword}</div>
                  </td>
                  <td><span className={`crm-seo-status crm-seo-status--${a.status}`}>{STATUS_LABEL[a.status] || a.status}</span></td>
                  <td className="crm-seo-cost">{formatCost(a.generation_cost_cents)}</td>
                  <td className="crm-seo-rowactions">
                    {a.status === 'draft' || a.status === 'needs_review' ? (
                      <button className="crm-seo-link" disabled={busy} onClick={() => approve(a)}>Approve</button>
                    ) : null}
                    {a.status === 'approved' || a.status === 'failed' ? (
                      <button className="crm-seo-link" disabled={busy} onClick={() => publish(a)}>
                        {wpConfigured ? 'Publish' : 'Publish…'}
                      </button>
                    ) : null}
                    {a.wp_url ? <a className="crm-seo-link" href={a.wp_url} target="_blank" rel="noopener noreferrer">View</a> : null}
                    <button className="crm-seo-link crm-seo-link--danger" disabled={busy} onClick={() => remove(a)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor / preview modal */}
      {editor && (
        <div className="crm-seo-overlay" onClick={() => setEditor(null)}>
          <div className="crm-seo-modal" onClick={e => e.stopPropagation()}>
            <div className="crm-seo-modal-head">
              <strong>Review article</strong>
              <button className="crm-seo-link" onClick={() => setEditor(null)}>close</button>
            </div>

            {Array.isArray(lintFlags) && lintFlags.length > 0 && (
              <div className="crm-seo-banner crm-seo-banner--warn">
                Style flags to review: {lintFlags.join(', ')}
              </div>
            )}

            <div className="crm-seo-modal-body">
              <div className="crm-seo-edit">
                <label>Title</label>
                <input type="text" value={editor.title || ''} onChange={e => setEditor({ ...editor, title: e.target.value })} />
                <label>Meta title ({(editor.meta_title || '').length}/60)</label>
                <input type="text" value={editor.meta_title || ''} onChange={e => setEditor({ ...editor, meta_title: e.target.value })} />
                <label>Meta description ({(editor.meta_description || '').length}/155)</label>
                <textarea rows={2} value={editor.meta_description || ''} onChange={e => setEditor({ ...editor, meta_description: e.target.value })} />
                <div className="crm-seo-row">
                  <div style={{ flex: 1 }}>
                    <label>Target keyword</label>
                    <input type="text" value={editor.target_keyword || ''} onChange={e => setEditor({ ...editor, target_keyword: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>Category</label>
                    <input type="text" list="crm-seo-cats" value={editor.category || ''} onChange={e => setEditor({ ...editor, category: e.target.value })} />
                    <datalist id="crm-seo-cats">{categories.map(c => <option key={c} value={c} />)}</datalist>
                  </div>
                </div>
                <label>Slug</label>
                <input type="text" value={editor.slug || ''} onChange={e => setEditor({ ...editor, slug: e.target.value })} />
                <label>CTA URL</label>
                <input type="text" value={editor.cta_url || ''} onChange={e => setEditor({ ...editor, cta_url: e.target.value })} />
                <label>Body HTML</label>
                <textarea className="crm-seo-html" rows={16} value={editor.body_html || ''} onChange={e => setEditor({ ...editor, body_html: e.target.value })} />
                <button className="crm-seo-btn" disabled={editorBusy} onClick={saveEditor}>{editorBusy ? 'Saving…' : 'Save changes'}</button>
              </div>

              <div className="crm-seo-preview">
                <div className="crm-seo-preview-label">Preview</div>
                <div className="crm-seo-featured">
                  {editor.featured_image_url ? (
                    <img className="crm-seo-featured-img" src={editor.featured_image_url} alt="" />
                  ) : (
                    <div className="crm-seo-featured-empty">No featured image yet</div>
                  )}
                  <button className="crm-seo-btn" disabled={imageBusy || !aiConfigured} onClick={genImage}>
                    {imageBusy ? 'Generating…' : editor.featured_image_url ? 'Regenerate image' : 'Generate featured image'}
                  </button>
                </div>
                <div className="crm-seo-article">
                  <h1>{editor.title}</h1>
                  <div dangerouslySetInnerHTML={{ __html: editor.body_html || '' }} />
                  {Array.isArray(editor.faq) && editor.faq.length > 0 && (
                    <div className="crm-seo-faq">
                      <h2>Frequently asked questions</h2>
                      {editor.faq.map((f, i) => (
                        <div key={i}>
                          <h3>{f.q}</h3>
                          <p>{f.a}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="crm-seo-modal-foot">
              <span className={`crm-seo-status crm-seo-status--${editor.status}`}>{STATUS_LABEL[editor.status] || editor.status}</span>
              <span className="crm-seo-cost">Cost {formatCost(editor.generation_cost_cents)}</span>
              <div className="crm-seo-foot-actions">
                {(editor.status === 'draft' || editor.status === 'needs_review') && (
                  <button className="crm-seo-btn" disabled={busy} onClick={() => approve(editor)}>Approve</button>
                )}
                {(editor.status === 'approved' || editor.status === 'failed') && (
                  <button className="crm-seo-btn crm-seo-btn--primary" disabled={busy} onClick={() => publish(editor)}>
                    {wpConfigured ? 'Publish to site' : 'Publish (not connected)'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
